import React from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Mail, Clock, Facebook, Instagram } from "lucide-react";
import { useContent } from "@/context/ContentContext";

export default function Footer() {
    const { content } = useContent();
    if (!content) return null;

    const phone = content.phone;
    const phoneHref = `tel:${phone.replace(/[^+0-9]/g, "")}`;

    return (
        <footer className="bg-tbv-ink text-white" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-6 py-16">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
                    <div className="md:col-span-2">
                        <div className="flex items-center gap-3 mb-5">
                            {content.logo_url && (
                                <img
                                    src={content.logo_url}
                                    alt="Three Brothers Vivero"
                                    className="w-14 h-14 object-contain rounded-lg bg-black p-1"
                                />
                            )}
                            <div>
                                <div className="font-condensed text-2xl">THREE BROTHERS</div>
                                <div className="font-condensed text-sm text-tbv-green -mt-1">NURSERY</div>
                            </div>
                        </div>
                        <p className="text-white/70 max-w-md mb-6">
                            {content.about_text}
                        </p>
                        <div className="flex gap-3">
                            {content.facebook_url && (
                                <a href={content.facebook_url} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-tbv-green hover:text-tbv-ink flex items-center justify-center transition" data-testid="footer-facebook">
                                    <Facebook className="w-4 h-4" />
                                </a>
                            )}
                            {content.instagram_url && (
                                <a href={content.instagram_url} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-tbv-green hover:text-tbv-ink flex items-center justify-center transition" data-testid="footer-instagram">
                                    <Instagram className="w-4 h-4" />
                                </a>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="font-display font-bold uppercase text-xs tracking-widest text-tbv-green mb-4">Contact</div>
                        <ul className="space-y-3 text-sm text-white/80">
                            {(content.contacts && content.contacts.length > 0
                                ? content.contacts
                                : [{ id: "main", name: "", phone: content.phone }]
                            ).map((c, i) => (
                                <li key={c.id || i} className="flex items-start gap-3">
                                    <Phone className="w-4 h-4 mt-1 text-tbv-green flex-shrink-0" />
                                    <a href={`tel:${c.phone.replace(/[^+0-9]/g, "")}`} data-testid={`footer-phone-${i}`}>
                                        {c.name && <span className="text-white/60 mr-1">{c.name}:</span>}
                                        {c.phone}
                                    </a>
                                </li>
                            ))}
                            <li className="flex items-start gap-3">
                                <Mail className="w-4 h-4 mt-1 text-tbv-green flex-shrink-0" />
                                <a href={`mailto:${content.email}`} className="break-all">{content.email}</a>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 mt-1 text-tbv-green flex-shrink-0" />
                                <span>{content.address}</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Clock className="w-4 h-4 mt-1 text-tbv-green flex-shrink-0" />
                                <span>{content.hours}</span>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <div className="font-display font-bold uppercase text-xs tracking-widest text-tbv-green mb-4">Service Areas</div>
                        <p className="text-sm text-white/70 mb-6">{content.service_area}</p>
                        <Link to="/admin/login" className="text-xs text-white/40 hover:text-white/80 transition" data-testid="footer-admin-link">
                            Admin Panel
                        </Link>
                    </div>
                </div>

                <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
                    <div>© {new Date().getFullYear()} {content.business_name}. All rights reserved.</div>
                    <div>East Bernard, Texas · Nursery, Landscaping & Tree Services</div>
                </div>
            </div>
        </footer>
    );
}
